import { useEffect, useState } from 'react';
import { Loader2, AlertCircle, Layers, Lock, Flame } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { cn } from '@/lib/utils';
import { API_BASE } from '@/lib/api';

interface CardStats {
  totalCards: number;
  lockedCards: number;
  burnedCards: number;
  byRarity: Record<string, number>;
}

interface RecentMint {
  id: string;
  playerId: string;
  playerName: string;
  team: string;
  position: string;
  rarity: string;
  serialNumber: number | null;
  collection: string;
  season: number;
  isLocked: boolean;
  isBurned: boolean;
  ownerId: string | null;
  ownerUsername: string | null;
  createdAt: string;
}

const RARITY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  CORE: { bg: 'bg-zinc-500/10', text: 'text-zinc-400', border: 'border-zinc-500/30' },
  RARE: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/30' },
  ELITE: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/30' },
  LEGENDARY: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30' },
  PRIME: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30' },
};

export default function AdminCardsPage() {
  const { accessToken } = useAuthStore();
  const [stats, setStats] = useState<CardStats | null>(null);
  const [recentMints, setRecentMints] = useState<RecentMint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchCardStats() {
      if (!accessToken) return;

      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE}/api/admin/cards`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch card stats');
        }

        const data = await response.json();
        setStats(data.data.stats);
        setRecentMints(data.data.recentMints);
      } catch (err) {
        console.error('Failed to fetch card stats:', err);
        setError('Failed to load card statistics');
      } finally {
        setIsLoading(false);
      }
    }

    fetchCardStats();
  }, [accessToken]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getRarityBadge = (rarity: string) => {
    const colors = RARITY_COLORS[rarity] || RARITY_COLORS.CORE;
    return (
      <span
        className={cn(
          'px-2 py-0.5 text-xs font-medium rounded-full border',
          colors.bg,
          colors.text,
          colors.border
        )}
      >
        {rarity}
      </span>
    );
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Cards</h1>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
        </div>
      )}

      {!isLoading && stats && (
        <>
          {/* Overview Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
              <div className="flex items-center gap-3 mb-2">
                <div className="h-10 w-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Layers className="h-5 w-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.totalCards.toLocaleString()}</p>
                  <p className="text-sm text-zinc-400">Total Cards</p>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
              <div className="flex items-center gap-3 mb-2">
                <div className="h-10 w-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                  <Lock className="h-5 w-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.lockedCards.toLocaleString()}</p>
                  <p className="text-sm text-zinc-400">Locked in Competition</p>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
              <div className="flex items-center gap-3 mb-2">
                <div className="h-10 w-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <Flame className="h-5 w-5 text-red-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.burnedCards.toLocaleString()}</p>
                  <p className="text-sm text-zinc-400">Burned</p>
                </div>
              </div>
            </div>
          </div>

          {/* Rarity Breakdown */}
          <div className="rounded-xl bg-zinc-900 border border-zinc-800 p-4 mb-8">
            <h2 className="text-lg font-semibold text-white mb-4">Supply by Rarity</h2>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {Object.entries(stats.byRarity).map(([rarity, count]) => {
                const colors = RARITY_COLORS[rarity] || RARITY_COLORS.CORE;
                return (
                  <div
                    key={rarity}
                    className={cn(
                      'p-4 rounded-lg border',
                      colors.bg,
                      colors.border
                    )}
                  >
                    <p className={cn('text-2xl font-bold', colors.text)}>
                      {count.toLocaleString()}
                    </p>
                    <p className="text-sm text-zinc-400">{rarity}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recent Mints */}
          <div className="rounded-xl bg-zinc-900 border border-zinc-800 overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-800">
              <h2 className="text-lg font-semibold text-white">Recent Mints</h2>
            </div>

            {recentMints.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                        Player
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                        Rarity
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                        Serial
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                        Owner
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                        Minted
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    {recentMints.map((card) => (
                      <tr key={card.id} className="hover:bg-zinc-800/50 transition-colors">
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-white">{card.playerName}</p>
                            <p className="text-xs text-zinc-500">
                              {card.position} - {card.team}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-3">{getRarityBadge(card.rarity)}</td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-zinc-300">
                            {card.serialNumber ? `#${card.serialNumber}` : '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {card.isBurned ? (
                            <span className="px-2 py-0.5 text-xs font-medium rounded bg-red-500/10 text-red-400">
                              Burned
                            </span>
                          ) : card.isLocked ? (
                            <span className="px-2 py-0.5 text-xs font-medium rounded bg-yellow-500/10 text-yellow-400">
                              Locked
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 text-xs font-medium rounded bg-green-500/10 text-green-400">
                              Active
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-zinc-400">
                            {card.ownerUsername || 'Unclaimed'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-zinc-400">{formatDate(card.createdAt)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <Layers className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No cards minted yet</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
